import { useEffect, useRef, useState } from 'react';
import { Modal, ModalContent } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { AudioLines, Bell, Check, Clock, Dumbbell, Pause, Play, X } from 'lucide-react';

import { TALK_MOMENTS } from '../../utils/alexaTalk';
import { prefersReducedMotion } from '../../utils/motion';
import { trackEvent } from '../../utils/gt';

const NOTE_ICONS = { set: Dumbbell, reminder: Bell, later: Clock, done: Check };

// A plain fade: NextUI's slide-up on phones stays stuck 80px down when the device asks for reduced motion
const FADE = {
  variants: {
    enter: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2, ease: 'easeOut' } },
    exit: { opacity: 0, y: 0, scale: 1, transition: { duration: 0.15, ease: 'easeIn' } },
  },
};

// "Ver acontecer" pacing: a beat before the first line, Alexa "typing" before each of hers, and a pause after
// each line long enough to read it
const FIRST_BEAT = 300;
const TYPING = 900;
const AFTER = { note: 700, you: 900, alexa: 1500 };

const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

const cancelAll = (timers) => {
  timers.current.forEach(clearTimeout);
  timers.current = [];
};

const TalkLine = ({ line, startsMoment, isLit, talk }) => {
  const NoteIcon = NOTE_ICONS[line.icon];
  return (
    <li
      className={`mt-ax-line mt-ax-line--${line.who}${startsMoment ? ' is-moment-start' : ''}${isLit ? ' is-lit' : ''}`}
      data-moment={startsMoment ? line.moment : undefined}
    >
      {line.who === 'alexa' && (
        <span className="mt-ax-line__av" aria-hidden="true">
          <AudioLines />
        </span>
      )}
      <p>
        {line.who === 'note' ? (
          NoteIcon && <NoteIcon className={line.icon === 'set' ? 'is-dumbbell' : ''} aria-hidden="true" />
        ) : (
          <span className="mt-sr-only">{`${talk(line.who)}: `}</span>
        )}
        {line.text}
      </p>
    </li>
  );
};

/**
 * A session with the Alexa skill told as a chat (owner, 29/10: the conversation of proposal "B · Conversa" in a sheet
 * over the guide). The moments jump to their part of the session; "Ver acontecer" plays it line by line. Reduced
 * motion keeps the pacing (it's what's being shown) but drops the line's rise, the dots' pulse and smooth scrolling
 */
export const AlexaTalkSheet = ({ isOpen, onClose, lines, subtitle }) => {
  const { t } = useTranslation();
  const talk = (key, options) => t(`pages.Tutorial.talk.${key}`, options);
  const isPhone = useMediaQuery({ query: '(max-width: 640px)' });
  const [moment, setMoment] = useState('');
  // How many lines show while playing; null when all of them do
  const [shown, setShown] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const timers = useRef([]);
  const chatRef = useRef(null);
  const isPlaying = shown !== null;

  const stop = () => {
    cancelAll(timers);
    setShown(null);
    setIsTyping(false);
  };

  useEffect(() => {
    const pending = timers;
    return () => cancelAll(pending);
  }, []);

  // Every opening starts from the whole conversation
  useEffect(() => {
    if (isOpen) return;
    cancelAll(timers);
    setShown(null);
    setIsTyping(false);
    setMoment('');
  }, [isOpen]);

  // While playing, the newest line (or Alexa's dots) stays in view
  useEffect(() => {
    if (!isPlaying) return;
    const newest = chatRef.current?.lastElementChild;
    newest?.scrollIntoView?.({ block: 'nearest', behavior: scrollBehavior() });
  }, [shown, isTyping, isPlaying]);

  // A moment scrolls the chat to its first line
  useEffect(() => {
    const chat = chatRef.current;
    const first = moment && chat?.querySelector(`[data-moment="${moment}"]`);
    if (first) chat.scrollTo?.({ top: first.offsetTop - 12, behavior: scrollBehavior() });
  }, [moment]);

  const play = () => {
    trackEvent('sync_demo_play', 'Sync - Demo Play', 'sync', true);
    cancelAll(timers);
    setMoment('');
    setShown(0);
    setIsTyping(false);
    chatRef.current?.scrollTo?.({ top: 0 });
    let at = FIRST_BEAT;
    lines.forEach((line, index) => {
      if (line.who === 'alexa') {
        timers.current.push(setTimeout(() => setIsTyping(true), at));
        at += TYPING;
      }
      timers.current.push(
        setTimeout(() => {
          setIsTyping(false);
          setShown(index + 1 === lines.length ? null : index + 1);
        }, at)
      );
      at += AFTER[line.who];
    });
  };

  const pickMoment = (key) => {
    if (isPlaying) stop();
    trackEvent('sync_demo_moment', `Sync - Demo Moment: ${key}`, 'sync', true);
    setMoment((current) => (current === key ? '' : key));
  };

  const visible = isPlaying ? lines.slice(0, shown) : lines;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size={isPhone ? 'full' : '3xl'}
      placement="center"
      scrollBehavior="inside"
      hideCloseButton
      motionProps={FADE}
      // NextUI drops aria-labelledby without its own ModalHeader: the dialog is named here
      aria-label={talk('title')}
      classNames={{ base: 'mt-root mt-sheet mt-ax-talk', backdrop: 'mt-dialog__backdrop' }}
    >
      <ModalContent>
        {(close) => (
          <>
            <div className="mt-ax-talk__head">
              <div className="mt-ax-talk__top">
                <h2 id="mt-ax-talk-title" className="mt-ax-talk__title">
                  {talk('title')}
                </h2>
                <button type="button" className="mt-icon-btn" onClick={close} aria-label={talk('close')}>
                  <X aria-hidden="true" />
                </button>
              </div>
              <p className="mt-ax-talk__sub">{subtitle}</p>
              <div className="mt-ax-talk__controls">
                <button type="button" className="mt-btn mt-btn--quiet mt-ax-talk__play" onClick={isPlaying ? stop : play}>
                  {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
                  {isPlaying ? talk('stop') : talk('play')}
                </button>
                <div className="mt-ax-moments" role="group" aria-label={talk('momentsLabel')}>
                  {TALK_MOMENTS.map((key) => (
                    <button key={key} type="button" className="mt-ax-moment" aria-pressed={moment === key} onClick={() => pickMoment(key)}>
                      {talk(`moments.${key}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <ol
              ref={chatRef}
              className={`mt-ax-chat${moment ? ' has-moment' : ''}${isPlaying ? ' is-playing' : ''}`}
              aria-label={talk('chatLabel')}
              aria-live="polite"
            >
              {visible.map((line, index) => (
                <TalkLine
                  key={index}
                  line={line}
                  startsMoment={index === 0 || visible[index - 1].moment !== line.moment}
                  isLit={line.moment === moment}
                  talk={talk}
                />
              ))}
              {isTyping && (
                <li className="mt-ax-line mt-ax-line--alexa mt-ax-line--typing" aria-hidden="true">
                  <span className="mt-ax-line__av">
                    <AudioLines />
                  </span>
                  <span className="mt-ax-dots">
                    <i />
                    <i />
                    <i />
                  </span>
                </li>
              )}
            </ol>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
